import {
  connectAuthEmulator,
  getAuth,
  signOut,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile,
  User,
} from 'firebase/auth'
import { initializeApp, FirebaseApp, FirebaseError } from 'firebase/app'

import { railsFetch } from '@/util/rails_fetch'
import { getCsrfToken } from '@/util/csrf_token'
import { assertDefined } from '@/util/assert'
import { i18n } from '@/libraries/i18n'
import { firePlausibleGoal } from '@/libraries/plausible'
import { pageData } from '@/util/page_data'
import { authErrorMessage } from './auth_error_message'

function getCookie(name: string): string | undefined {
  return Object.fromEntries(document.cookie.split('; ').map((v) => v.split(/=(.*)/s).map(decodeURIComponent)))[name]
}

function verifyEmailPath(): string {
  const language = getCookie('language')
  return language ? `/verify_email?lang=${encodeURIComponent(language)}` : '/verify_email'
}

// Firebase keeps its session in indexedDB, which cannot be read synchronously, so the landing pages cannot tell
// whether they are about to redirect a signed-in visitor until this bundle has loaded and Firebase has answered.
// This marker can be read before the first paint, which is what Authentication::Component::SigningIn does.
const SIGNED_IN_MARKER = 'luca.firebaseSignedIn'
const SIGNING_IN_CLASS = 'is-signing-in'

function rememberSignedIn(signedIn: boolean) {
  try {
    if (signedIn) {
      window.localStorage.setItem(SIGNED_IN_MARKER, '1')
    } else {
      window.localStorage.removeItem(SIGNED_IN_MARKER)
    }
  } catch {
    // Storage can be blocked entirely (Safari, cookies off). We then simply always render the form.
  }
}

// The ID token stays valid for an hour even after signing out, so it must never be put in a URL, which browser
// history, access logs and analytics all keep.
function signInToLuca(idToken: string, returnTo?: string) {
  const fields: Record<string, string> = { token: idToken, lang: document.documentElement.lang }
  if (returnTo !== undefined) fields.return_to = returnTo
  submitToLuca('/authentication/session', fields)
}

function submitToLuca(action: string, fields: Record<string, string>) {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = action
  form.dataset.turbo = 'false'
  for (const [name, value] of Object.entries({ authenticity_token: getCsrfToken(), ...fields })) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.append(input)
  }
  document.body.append(form)
  form.submit()
}

export default class LucaLogin {
  signInButton!: HTMLButtonElement
  signOutButton!: HTMLButtonElement
  signOutButtonMobile!: HTMLButtonElement
  signInPasswordButton!: HTMLButtonElement
  createWithPasswordButton!: HTMLButtonElement
  sendEmailVerificationButton!: HTMLButtonElement
  cancelSignInButton!: HTMLAnchorElement
  alertDiv!: HTMLElement | null
  alertText!: HTMLElement | null
  creatingUser = false
  #firebaseApp!: FirebaseApp

  constructor() {
    const { firebase } = pageData()
    if (firebase) {
      this.#firebaseApp = initializeApp(firebase)
      if (firebase.authEmulatorHost) {
        connectAuthEmulator(getAuth(this.#firebaseApp), `http://${firebase.authEmulatorHost}`, {
          disableWarnings: true,
        })
      }
    }
    this.setupAllButtons()
  }

  setupAllButtons() {
    this.setupLogoutButton()

    this.signInButton = document.getElementById('sign-in-google') as HTMLButtonElement
    if (this.signInButton != null) {
      this.signInButton.addEventListener('click', this.signInGoogle)
    }

    this.signInPasswordButton = document.getElementById('sign-in-password') as HTMLButtonElement
    if (this.signInPasswordButton != null) {
      this.signInPasswordButton.addEventListener('click', this.signInPassword)
    }
    this.createWithPasswordButton = document.getElementById('create_password') as HTMLButtonElement
    if (this.createWithPasswordButton != null) {
      this.createWithPasswordButton.addEventListener('click', this.createWithPassword)
      // A page restored from the back-forward cache comes back exactly as it was left, mid-sign-up.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.createWithPasswordButton.disabled = false
      })
    }
    this.sendEmailVerificationButton = document.getElementById('send-verification') as HTMLButtonElement
    if (this.sendEmailVerificationButton != null) {
      this.sendEmailVerificationButton.addEventListener('click', this.sendEmailVerificationClick)
    }
    this.cancelSignInButton = document.getElementById('cancel-sign-in') as HTMLAnchorElement
    if (this.cancelSignInButton != null) {
      this.cancelSignInButton.addEventListener('click', this.cancelSignIn)
    }
    this.alertDiv = document.getElementById('alert-box')
    this.alertText = document.getElementById('alert-text')
  }

  setupLogoutButton() {
    // We might have two sign out buttons on the page.
    this.signOutButton = document.getElementById('sign-out') as HTMLButtonElement
    if (this.signOutButton != null) {
      this.signOutButton.addEventListener('click', this.signOut)
    }

    this.signOutButtonMobile = document.getElementById('sign-out-mobile') as HTMLButtonElement
    if (this.signOutButtonMobile != null) {
      this.signOutButtonMobile.addEventListener('click', this.signOut)
    }
  }

  async listenToAuthChange() {
    const auth = getAuth(this.#firebaseApp)
    // Signed out before listening, or the listener would post the refused token again.
    if (document.querySelector('[data-sign-out-of-firebase]') !== null) {
      await signOut(auth)
      rememberSignedIn(false)
    }
    onAuthStateChanged(auth, this.onAuthStateChanged)
  }

  signInGoogle = () => {
    this.setSignupPathCookie()
    // Sign in Firebase using popup auth and Google as the identity provider.
    const provider = new GoogleAuthProvider()
    signInWithPopup(getAuth(this.#firebaseApp), provider).catch((error) => this.#showError(error))
  }

  // Some of the logged-out pages that load this have no sign-in form, and so nowhere to show a message.
  setAlert(msg: string) {
    if (this.alertDiv === null || this.alertText === null) return

    this.alertDiv.classList.remove('d-none')
    this.alertText.textContent = msg
  }

  #showError(error: unknown, options?: { registering: boolean }) {
    const message = authErrorMessage(error, options)
    if (message !== null) this.setAlert(message)
  }

  signInPassword = (event: Event) => {
    event.preventDefault()
    const email = (document.getElementById('email') as HTMLInputElement).value
    const password = (document.getElementById('password') as HTMLInputElement).value
    this.validateCredentials(email, password)

    return this.signInWithEmailAndPassword(email, password, false)
  }

  validateCredentials(email: string, password: string) {
    if (email.length < 4) {
      alert('Please enter an email address.')
      return
    }
    if (password.length < 4) {
      alert('Please enter a password.')
      return
    }
  }

  createWithPassword = async (event: Event) => {
    event.preventDefault()

    const email = (document.getElementById('email') as HTMLInputElement).value
    const password = (document.getElementById('password') as HTMLInputElement).value

    if (email.length < 4) {
      alert('Please enter an email address.')
      return
    }
    if (password.length < 4) {
      alert('Please enter a password.')
      return
    }

    // Signing up takes seconds and ends with the page leaving for /verify_email, which cancels whatever a second
    // click starts in the meantime.
    this.createWithPasswordButton.disabled = true
    let leavingPage = false
    try {
      let result
      try {
        result = await this.signOutBlockedUserCheck(email)
      } catch (error) {
        // fetch rejects with a TypeError when the request got no answer
        if (!(error instanceof TypeError)) throw error
        this.setAlert(i18n.t('authentication.network_request_failed'))
        return
      }
      if (result.status) {
        this.setAlert(i18n.t('authentication.blocked_email'))
        return
      }
      leavingPage = await this.#createUser(email, password)
    } finally {
      if (!leavingPage) this.createWithPasswordButton.disabled = false
    }
  }

  // True when the page is leaving for /verify_email
  async #createUser(email: string, password: string): Promise<boolean> {
    try {
      firePlausibleGoal('user#create')

      // We set creatingUser to true or else onAuthStateChanged will redirect us to verified_email before
      // createUserWithEmailAndPassword returns.
      this.creatingUser = true
      await createUserWithEmailAndPassword(getAuth(this.#firebaseApp), email, password)
      let user = getAuth(this.#firebaseApp).currentUser
      assertDefined(user, 'currentUser must be set after createUserWithEmailAndPassword')

      const fullnameElement = document.getElementById('fullname') as HTMLInputElement
      if (fullnameElement !== null) {
        const fullname = fullnameElement.value
        await updateProfile(user, { displayName: fullname })
      }

      document.cookie = `verify_email=${email}`
      await this.sendEmailVerification(user)
      if (window.location.pathname !== '/verify_email') {
        window.location.href = verifyEmailPath()
        return true
      }

      this.creatingUser = false
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'auth/email-already-in-use') {
        this.signInWithEmailAndPassword(email, password, true)
      } else {
        this.#showError(error)
      }
    }
    return false
  }

  async signOutBlockedUserCheck(email: string) {
    const response = await fetch('/blocked_user?' + new URLSearchParams({ email: email }))
    return await response.json()
  }

  sendEmailVerificationClick = async (event: Event) => {
    this.setAlert(i18n.t('notifications.resent_confirmation_message'))
    event.preventDefault()
    try {
      const user = getAuth(this.#firebaseApp).currentUser
      assertDefined(user, 'currentUser must be set when resending verification email')
      await this.sendEmailVerification(user)
    } catch (error) {
      console.warn(error)
      this.setAlert(i18n.t('notifications.could_not_send_message_warning'))
    }
  }

  async sendEmailVerification(user: User) {
    await this.setSignupPathCookie()

    // Refreshed, so the token carries the name just set by updateProfile
    const token = await user.getIdToken(true)
    await railsFetch('/send_verification_email', {
      method: 'POST',
      body: JSON.stringify({
        lang: getCookie('language'),
        token,
        plan: getCookie('subscription_type'),
      }),
    })
  }

  signOut = async (event: Event) => {
    event.preventDefault()
    await signOut(getAuth(this.#firebaseApp))
    // The redirect below races the auth state listener, so drop the marker here rather than relying on it.
    rememberSignedIn(false)
    const action = window.location.pathname.startsWith('/found_company')
      ? '/found_company/authentications/sign_out_user'
      : '/sign_out_user'
    submitToLuca(action, { _method: 'delete' })
  }

  onAuthStateChanged = async (user: User | null) => {
    if (user) {
      rememberSignedIn(true)
      if (window.location.pathname.startsWith('/found_company/authentications')) {
        return this.onAuthStateChangedFoundCompany()
      } else if (window.location.pathname.startsWith('/user_invitations')) {
        // we shouldn't redirect the user to the company page if he haven't accepted an invitation
      } else if (window.location.pathname.endsWith('/edit_password')) {
        // This page is only reached by a signed-in user who still has to replace a one-time password, and Rails
        // sends them straight back here, so redirecting them to the dashboard would loop forever.
      } else if (user.email == null) {
        // Luca finds the user by the account's email address, so an account without one can never sign in.
        await signOut(getAuth(this.#firebaseApp))
        rememberSignedIn(false)
        this.setAlert(i18n.t('authentication.account_without_email'))
      } else if (user.emailVerified) {
        const currentUser = getAuth(this.#firebaseApp).currentUser
        assertDefined(currentUser, 'currentUser must match the user passed to onAuthStateChanged')
        // The browser keeps painting this page until the dashboard answers, which takes seconds, so the form
        // has to be covered before the token request goes out.
        this.showSigningIn()
        return currentUser
          .getIdToken(true)
          .then(function (idToken) {
            let urlWithParams = new URL(window.location.href)
            // User clicked the link for specific subcription via home page.
            const subType = urlWithParams.searchParams.get('sub_type')
            if (subType !== null) {
              document.cookie = `subscription_type=${subType}`
              signInToLuca(idToken, '/companies/new')
            } else {
              signInToLuca(idToken)
            }
          })
          .catch(this.#signInToLucaFailed)
      } else {
        // In this case we have a not verified email, send to verify email
        // But not if we are currently creating a user (this is run immediately when creating a user, even
        // before the await method returns
        if (!this.creatingUser) {
          if (window.location.pathname !== '/verify_email') {
            this.showSigningIn()
            return (window.location.href = verifyEmailPath())
          }
        }
      }
    } else {
      // Either nobody is signed in, or the marker outlived the Firebase session. Both mean: show the form.
      rememberSignedIn(false)
      this.hideSigningIn()
    }
  }

  showSigningIn() {
    if (window.lucaSigningInFallback !== undefined) {
      clearTimeout(window.lucaSigningInFallback)
      window.lucaSigningInFallback = undefined
    }
    document.documentElement.classList.add(SIGNING_IN_CLASS)
  }

  hideSigningIn() {
    document.documentElement.classList.remove(SIGNING_IN_CLASS)
  }

  cancelSignIn = async (event: Event) => {
    event.preventDefault()
    await signOut(getAuth(this.#firebaseApp))
    rememberSignedIn(false)
    this.hideSigningIn()
  }

  async onAuthStateChangedFoundCompany() {
    const currentUser = getAuth(this.#firebaseApp).currentUser
    assertDefined(currentUser, 'currentUser must be set when onAuthStateChanged dispatches to found-company')
    return currentUser
      .getIdToken(true)
      .then(async function (idToken) {
        let searchParams = new URL(window.location.href).searchParams
        let submissionId = searchParams.get('submission_id')
        let wizardToken = searchParams.get('wizard_token')

        if (submissionId != null) {
          // We get here from the "about you" step when the email already belongs to a user, so the submission
          // still has none and the wizard token is the only thing authorizing the request we bounce back to.
          const query = wizardToken != null ? `?${new URLSearchParams({ wizard_token: wizardToken })}` : ''
          signInToLuca(idToken, `/found_company/submissions/${submissionId}${query}`)
        } else {
          signInToLuca(idToken, '/found_company/submissions')
        }
      })
      .catch(this.#signInToLucaFailed)
  }

  // Signed out of Firebase, because signing in again as the same user would not fire onAuthStateChanged, and the
  // form would do nothing.
  #signInToLucaFailed = async (error: unknown) => {
    await signOut(getAuth(this.#firebaseApp))
    rememberSignedIn(false)
    this.hideSigningIn()
    this.#showError(error)
  }

  signInWithEmailAndPassword(email: string, password: string, registering: boolean) {
    signInWithEmailAndPassword(getAuth(this.#firebaseApp), email, password).catch((error) =>
      this.#showError(error, { registering }),
    )
  }

  setSignupPathCookie() {
    let pageUrl = window.location.href
    let urlWithParams = new URL(pageUrl)
    let lastUrlSegment = pageUrl.substring(pageUrl.lastIndexOf('/') + 1).replace('#', '')
    if (pageUrl.includes('signup') && lastUrlSegment !== 'signup') {
      document.cookie = `signup=${lastUrlSegment}; path=/`
    }

    const subType = urlWithParams.searchParams.get('sub_type')
    if (subType !== null) {
      document.cookie = `subscription_type=${subType}`
    }

    let language = urlWithParams.searchParams.get('lang')
    if (language !== null) {
      document.cookie = `language=${encodeURIComponent(language)}`
    }
  }
}
